import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { fetchData, normilizeFilename } from "../utils";
import * as endpoints from '../settings';
import { setNotification } from "./notificationsSlice";
import { getData, getDetails } from "./dataSlice";
import sortBy from "lodash.sortby";
import { showForm } from "./formSlice";
import { decamelize } from "humps";
import { schemas } from "./schemas";
import { find, size } from "lodash";

export const uploadContracts = createAsyncThunk(
    "uploadContracts",
    (file, { dispatch }) => {
        const fmData = new FormData();
        fmData.append("file", file);
        fetchData(endpoints.API_UPLOAD_CONTRACTS, {
            method: "put",
            body: fmData
        }, schemas.UPLOAD_CONTRACTS).then(response => {
            let parts = window.location.href.split("/"),
            page = parts[parts.indexOf("#") + 1];
            if (page === "contracts") {
                dispatch(getData({ tab: page }));
            };
            dispatch(setNotification({
                message: "Contracts uploaded successfully",
                success: true,
                closable: false,
                log: Object.values(response.entities.contracts)
            }));
        }).catch(e => {
            dispatch(setNotification(e));
        })
    }
);

const downloadFile = createAsyncThunk(
    "dowloadFile",
    async (uuid, { dispatch }) => {
        try {
            const response = await fetchData(endpoints.API_PACKAGE_DOWNLOAD + "/" + uuid, {}, {});
            let a = document.createElement("a");
            a.download = response.result.saveAs;
            a.href = response.result.url;
            a.rel = "noopener noreferrer";
            a.target = "_blank";
            a.click();
            a.remove();
        } catch(e) {
            dispatch(setNotification(e));
        }
});

export const downloadPackage = createAsyncThunk(
    "downloadPackage",
    async({ id, minos, prereq }, { dispatch }) => {
        try {
            const response = await fetchData(endpoints.API_PACKAGE_DOWNLOAD, {
                headers: {
                    "X-Pkg-Id": id,
                    "X-Os-Version": minos
                }
            }, {});
            let urls = response.result.urls;
            if (prereq) {
                sortBy(urls, ["order"]).map(url => {
                    dispatch(downloadFile(url.url));
                })
            } else {
                let url = sortBy(urls, ["order"]).pop();
                dispatch(downloadFile(url.url));
            }
        } catch(e) {
            dispatch(setNotification(e));
        }
    }
);

/**
 * Three phases:
 *  1. check input fields: try to insert record immediately.
 *  2. Start upload and md5 calculation at the same time. Progress shows minimum of them.
 *  2.a When packages tab is showed, refresh view.
 *  2.b TODO Delete package record on any error adn 2.a.
 *  3. Save md5 value to db.
 */
export const registerPackage = createAsyncThunk(
    "registerPackage",
    async ({ data, files }, { dispatch, rejectWithValue, signal }) => {
        dispatch(showForm(false));
        try {
            // Create package record in Thermos Db
            const fmData = new FormData();
            fmData.append("files", JSON.stringify(files.map(file => ({
                    name: file.name,
                    size: file.size,
                    last_modified: file.lastModified,
                    save_as: file.name
                })
            )));
            fmData.append("data", JSON.stringify(data));
            const response = await fetchData(endpoints.API_PACKAGE_REGISTER, {
                method: "put",
                body: fmData
            }, {});

            let path = window.location.href.split("/");
            if (path[path.length - 1] === "packages") {
                dispatch(getData({ tab: "packages" }));
            };

            if (size(response.result)) {
                Promise.all(Object.values(response.result.files).map(res => {
                    let file = find(files, file => (normilizeFilename(file.name)) == res.fields.key.split("/")[1]);
                    if (file) {
                        const id = res.packageFileNumber;
                        dispatch(setUploadProgress({id: id, progress: 0}));
                        return Promise.all([
                            calcMd5({
                                id: id,
                                file: file,
                                dispatch,
                                signal
                            }),
                            uploadPackage({
                                id: id,
                                file: file,
                                data: data,
                                fields: res.fields,
                                url: res.url,
                                dispatch,
                                signal
                            })
                        ]).then(() => {
                            let path = window.location.href,
                            parts  = path.match(/(\/#\/packages\/)(\d+)$/);
                            if (parts && parts.length == 3) {
                                dispatch(getDetails({ page: 'packages', selected: parts[2] }));
                            }
                        });
                    } else {
                        throw new Error("File not found")
                    };
                })).then(() => {
                    dispatch(setNotification({ success: true, message: "Resource created successfully" }));
                }).catch(error => {
                    dispatch(setNotification(error));
                });
            }
            return {};

        } catch(e) {
            dispatch(setNotification(e));
        };
        return rejectWithValue({});
});

const uploadPackage = ({ id, file, fields, url, dispatch, signal }) => {
    return new Promise((resolve, reject) => {

        const xhr = new XMLHttpRequest();
        signal.addEventListener("abort", () => xhr.abort());

        const fmData = new FormData();
        Object.keys(fields).forEach(key => {
            fmData.append(decamelize(key, { separator: "-" }), fields[key]);
        });
        fmData.append("file", file);

        if (xhr.upload) {
            xhr.upload.onprogress = (e) => {
                let done = e.position || e.loaded, total = e.totalSize || e.total;
                let percent = Math.floor(done / total * 1000) / 10;
                dispatch(setUploadProgress({ id: id, progress: percent }));
            };
        };

        xhr.onload = function (e) {
            let code = xhr.status;
            if (code >= 200 && code <= 299) {
                dispatch(successUploadFile(id));
                resolve(id);
            } else {
                dispatch(failureUploadFile(id));
                let err = {};
                err.code = code;
                err.type = "error";
                let message = xhr.responseText.match("<Message>(.*?)</Message>");
                err.message = Array.isArray(message) ? message[1] : message;
                reject(err);
            }
        };

        xhr.onabort = function () {
            dispatch(cancelUploadFile(id));
        };

        xhr.onerror = () => {
            dispatch(failureUploadFile(id));
            let err = {};
            err.code = xhr.status;
            err.type = "error";
            let message = xhr.responseText.match("<Message>(.*?)</Message>");
            err.message = Array.isArray(message) ? message[1] : message;
            dispatch(setNotification(err));
        };

        xhr.open("post", url, true);
        xhr.send(fmData);
    });
}

const calcMd5 = ({ id, file, dispatch, signal }) => {
    return new Promise((resolve, reject) => {
        signal.addEventListener("abort", () => worker.terminate());
        dispatch(setMd5Progress({ id: id, md5progress: 0 }));

        const worker = new Worker(`${process.env.PUBLIC_URL}/js/md5-worker.js`);
        worker.postMessage({ file });
        worker.onerror = (err) => {
            reject(err);
        };
        worker.onmessage = (e) => {
            const { currentPart, totalParts, hashResult } = e.data;
            let percent = Math.floor(currentPart / totalParts * 1000) / 10;
            dispatch(setMd5Progress({ id: id, md5progress: percent }));
            if (percent == 100 && hashResult) {
                worker.terminate();
                dispatch(successUploadFile(id));
                resolve(updatePackageMd5({
                    id: id,
                    md5: hashResult,
                    file: file,
                    dispatch
                }));
            }
        };
    });
};

const updatePackageMd5 = ({id, md5, dispatch}) => {
    return fetchData(endpoints.API_PACKAGE_UPDATE_MD5, {
                method: "post",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    package_file_number: id,
                    md5: md5
                })
            }, {}
    )
}

const upload = createSlice({
    name: 'upload',
    initialState: {
        uploads: {
        // format will be like below
        // [id]: {
        //   id: [id],
        //   name: filename,
        //   progress: progress,
        //   md5progress: md5progress,
        //   status: 0 - sucessfully uploaded, -1 - failed, 1 - cancelled, 3 - processing,
        // }
        }
    },
    reducers: {
        setUploadProgress: (state, action) => {
            state.uploads[action.payload.id] = {
                ...state.uploads[action.payload.id],
                progress: action.payload.progress,
                status: 3
            }
        },
        setMd5Progress: (state, action) => {
            state.uploads[action.payload.id] = {
                ...state.uploads[action.payload.id],
                md5progress: action.payload.md5progress,
                status: 3
            }
        },
        inProcessUploadFile: (state, action) => {
            state.uploads[action.payload] = {
                ...state.uploads[action.payload],
                status: 3
            }
        },
        successUploadFile: (state, action) => {
            let upload = state.uploads[action.payload];
            if (upload && upload.md5progress == 100 && upload.progress == 100) {
                delete state.uploads[action.payload];
            }
        },
        failureUploadFile: (state, action) => {
            state.uploads[action.payload] = {
                ...state.uploads[action.payload],
                status: -1,
                progress: 0
            }
        },
        cancelUploadFile: (state, action) => {
            state.uploads[action.payload] = {
                ...state.uploads[action.payload],
                status: 1
            }
        }
    },
    extraReducers: {
        [downloadPackage.pending]: () => {
            document.body.style.cursor = "progress";
        },
        [downloadPackage.rejected]: () => {
            document.body.style.cursor = "default";
        },
        [downloadPackage.fulfilled]: () => {
            document.body.style.cursor = "default";
        },
        [downloadPackage.rejected]: () => {
            document.body.style.cursor = "default";
        }
    }
});

export const { setUploadFile, setUploadProgress, setMd5Progress, inProcessUploadFile, successUploadFile, failureUploadFile, cancelUploadFile } = upload.actions;
export default upload.reducer;